import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import PageHeaderIcon from "../../components/PageHeaderIcon";
import PillSearchField from "../../components/PillSearchField";
import SegmentedStatusFilter from "../../components/SegmentedStatusFilter";
import { fetchReviewLabTests } from "../../Services/apiService";
import { getProgressStatusStyle } from "../../theme/statusColors";
import * as XLSX from 'xlsx';

// Columns for Review page (same as Analysis page)
const getColumns = (navigate, updateMarkedStatus) => [
  { 
    field: "order_number", 
    headerName: "Order ID", 
    minWidth: 100, 
    flex: 0.8,

  },
  { 
    field: "test_identifier", 
    headerName: "Test ID", 
    minWidth: 120, 
    flex: 1 
  },
  {
    field: "returned_to_lab_date",
    headerName: "Date Received",
    minWidth: 120,
    flex: 0.9,
    renderCell: (params) => {
      if (!params.value) return "N/A";
      const date = new Date(params.value);
      return date.toLocaleDateString();
    },
  },
  {
    field: "progress_status",
    headerName: "Progress Status",
    minWidth: 130,
    flex: 1,
    renderCell: (params) => {
      const status = params.value;
      const displayStatus = !status || status === "" ? "Unknown" : status;

      const statusStyle = getProgressStatusStyle(displayStatus);

      return (
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          height: "100%",
          paddingLeft: 1
        }}>
          <Chip
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    ...statusStyle.dot,
                  }}
                />
                {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
              </Box>
            }
            sx={{
              ...statusStyle,
              fontSize: "0.75rem",
              height: "24px",
              "& .MuiChip-label": {
                px: 1,
              },
            }}
            size="small"
          />
        </Box>
      );
    },
  },
  {
    field: "marked_status",
    headerName: "Mark Order",
    sortable: false,
    align: "center",
    headerAlign: "center",
    minWidth: 150,
    flex: 1,
    renderCell: (params) => {
      const status = params.value;
      const displayStatus = !status || status === "" ? "None" : status;
      
      // Define colors and dot styles based on status
      const getStatusStyle = (status) => {
        switch (status) {
          case "None":
            return {
              backgroundColor: "#f5f5f5",
              color: "#666",
              border: "1px solid #ddd",
              dot: { backgroundColor: "#666", border: "none" }
            };
          case "Review":
            return {
              backgroundColor: "#fff8dc",
              color: "#000",
              border: "1px solid #f0d000",
              dot: { backgroundColor: "#f0d000", border: "none" }
            };
          default:
            return {
              backgroundColor: "#f5f5f5",
              color: "#666",
              border: "1px solid #ddd",
              dot: { backgroundColor: "#666", border: "none" }
            };
        }
      };

      const statusStyle = getStatusStyle(displayStatus);

      return (
        <Box sx={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          height: "100%" 
        }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={params.value || ""}
              onChange={(e) => {
                e.stopPropagation();
                console.log("Changing mark for order", params.row.order_number, "to", e.target.value);
                updateMarkedStatus(params.row.test_identifier, e.target.value);
              }}
              displayEmpty
              variant="standard"
              disableUnderline
              renderValue={() => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Chip
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            ...statusStyle.dot,
                          }}
                        />
                        {displayStatus}
                      </Box>
                    }
                    sx={{
                      ...statusStyle,
                      fontSize: "0.75rem",
                      height: "24px",
                      "& .MuiChip-label": {
                        px: 1,
                      },
                    }}
                    size="small"
                  />
                  <Box
                    sx={{
                      width: 0,
                      height: 0,
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderTop: '4px solid #666',
                      ml: 0.5,
                    }}
                  />
                </Box>
              )}
              sx={{
                "& .MuiSelect-select": {
                  paddingLeft: 0,
                  paddingRight: "0 !important",
                  textAlign: "center",
                  border: "none",
                  outline: "none",
                },
                "& .MuiSelect-icon": {
                  display: "none",
                },
                "& fieldset": {
                  border: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
            >
              <MenuItem value="None">
                <Chip
                  label="None"
                  sx={{
                    backgroundColor: "#f5f5f5",
                    color: "#666",
                    border: "1px solid #ddd",
                    fontSize: "0.75rem",
                    height: "24px",
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                  size="small"
                />
              </MenuItem>
              <MenuItem value="Review">
                <Chip
                  label="Review"
                  sx={{
                    backgroundColor: "#fff8dc",
                    color: "#000",
                    border: "1px solid #f0d000",
                    fontSize: "0.75rem",
                    height: "24px",
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                  size="small"
                />
              </MenuItem>
              
            </Select>
          </FormControl>
        </Box>
      );
    },
  },
];

function Review() {
  const [labTests, setLabTests] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const navigate = useNavigate();

  // Predefined progress status options (matching the Analysis page)
  const progressStatusOptions = [
    'dispatched',
    'received by customer',
    'returned to lab',
    'processing', 
    'analysis completed', 
    'completed', 
    'cancelled'
  ];

  // DataGrid columns - implement real updateMarkedStatus function
  const updateMarkedStatus = async (testIdentifier, newStatus) => {
    try {
      console.log("🔄 Updating marked status:", testIdentifier, "to", newStatus);
      
      // Find the row data to get order_number
      const rowData = labTests.find(item => item.test_identifier === testIdentifier);
      if (!rowData) {
        console.error('❌ Row data not found for test identifier:', testIdentifier);
        return;
      }

      const { order_number } = rowData;
      
      // Import markOrder API function
      const { markOrder } = await import("../../Services/apiService");
      
      // Call the API to update marked status in database
      await markOrder(order_number, testIdentifier, newStatus);
      console.log('✅ Successfully updated marked status in database');
      
      // Update local state
      if (newStatus === "Review") {
        // If changed to Review, update the local data
        setLabTests(prevData => 
          prevData.map(item => 
            item.test_identifier === testIdentifier 
              ? { ...item, marked_status: newStatus }
              : item
          )
        );
      } else {
        // If changed away from Review, remove from table since this page only shows Review orders
        setLabTests(prevData => 
          prevData.filter(item => item.test_identifier !== testIdentifier)
        );
        console.log('🗑️ Removed item from Review table as it is no longer marked as Review');
      }
      
    } catch (error) {
      console.error('❌ Error updating marked status:', error);
      // You could add a toast notification here to show the error to the user
    }
  };
  
  const columns = getColumns(navigate, updateMarkedStatus);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 30,
  });

  // Fetch review lab tests using API service
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("📡 Fetching Review Lab Tests...");
      const data = await fetchReviewLabTests();
      console.log("✅ Review Lab Tests data:", data);

      if (data.success && data.data) {
        // Transform data to include id field required by DataGrid
        // Keep all original API fields as they are - don't override them
        const transformedData = data.data.map((test, index) => ({
          ...test, // Keep all original fields exactly as they come from API
          id: test.test_identifier || test.lab_test_id || `test-${index}`, // Required for DataGrid
        }));
        console.log("🔄 Transformed data:", transformedData);
        
        // Filter to only show orders marked as "Review"
        const reviewOrdersOnly = transformedData.filter(test => 
          test.marked_status === "Review"
        );
        console.log("� Review orders only:", reviewOrdersOnly);
        
        setLabTests(reviewOrdersOnly);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("❌ Error fetching review lab tests:", err);
      setError(err.message);
      setLabTests([]);
    } finally {
      setLoading(false);
    }
  };

  // Export function to download Excel file
  const handleExportToExcel = () => {
    // Prepare data for export - use all labTests data (not just filtered)
    const exportData = labTests.map((test) => ({
      'Order ID': test.order_number || '',
      'Test ID': test.test_identifier || '',
      'Date Received': test.returned_to_lab_date ? new Date(test.returned_to_lab_date).toLocaleDateString() : 'N/A',
      'Progress Status': (() => {
        const status = test.progress_status;
        return !status || status === "" ? "Unknown" : status;
      })(),
      'Mark Order': (() => {
        const status = test.marked_status;
        return !status || status === "" ? "None" : status;
      })()
    }));

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Review');

    // Auto-size columns
    const colWidths = Object.keys(exportData[0] || {}).map(key => {
      const maxLength = Math.max(
        key.length,
        ...exportData.map(row => String(row[key] || '').length)
      );
      return { wch: Math.min(maxLength + 2, 30) }; // Cap at 30 characters
    });
    ws['!cols'] = colWidths;

    // Generate filename with current date
    const fileName = `review_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // Download file
    XLSX.writeFile(wb, fileName);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Client-side filtering effect
  useEffect(() => {
    let filtered = [...labTests];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => 
        item.order_number?.toString().toLowerCase().includes(query) ||
        item.test_identifier?.toString().toLowerCase().includes(query)
      );
    }

    // Apply status filter (using progress_status field)
    if (statusFilter) {
      filtered = filtered.filter((item) => {
        const itemStatus = item.progress_status?.toLowerCase().replace(/[_\s]/g, ' ').trim();
        return itemStatus === statusFilter.toLowerCase();
      });
    }

    console.log("🔍 Filtering results:", {
      originalCount: labTests.length,
      filteredCount: filtered.length,
      searchQuery,
      statusFilter,
      sampleData: filtered.slice(0, 2)
    });

    setFilteredData(filtered);
    
    // Reset to first page when filters change
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [labTests, searchQuery, statusFilter]);

  const handleRetry = () => {
    fetchData();
  };

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ px: 1, py: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
        <PageHeaderIcon src="/icons/ic_review.svg" />
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Review
        </Typography>

        <Button
          variant="contained"
          onClick={handleExportToExcel}
          disabled={loading || labTests.length === 0}
          sx={{ ml: "auto", textTransform: "none", borderRadius: 1.5, px: 2.5, py: 0.5, minHeight: 0, fontSize: "0.8125rem", backgroundColor: "#163666", color: "#fff", "&:hover": { backgroundColor: "#112B51" } }}
        >
          Export
        </Button>
      </Box>

      {/* Search + filters + actions row */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          rowGap: 1.5,
          flexWrap: "nowrap",
          alignItems: "center",
          mx: 1,
          mb: 2,
          p: 1.5,
          backgroundColor: "#FFFFFF",
          border: "1px solid #e5e7eb",
          borderRadius: 2,
          boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
          overflowX: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <PillSearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Enter order ID or test ID"
            resultCount={filteredData.length}
            loading={loading}
          />
        </Box>

        <Box sx={{ flexShrink: 0 }}>
          <SegmentedStatusFilter
            value={statusFilter}
            onChange={setStatusFilter}
            options={progressStatusOptions.map((opt) => ({ value: opt, label: opt.charAt(0).toUpperCase() + opt.slice(1) }))}
            disabled={loading}
          />
        </Box>
      </Box>

      {/* Data Table */}
      <Box sx={{ mx: 1, mb: 2, backgroundColor: "#FFFFFF", border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400
          }}>
            <CircularProgress sx={{ mr: 2 }} />
            <Typography>Loading review lab tests...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{
            p: 3,
            textAlign: 'center',
            minHeight: 400,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Typography color="error" variant="h6" sx={{ mb: 2 }}>
              Error loading review lab tests
            </Typography>
            <Typography color="error" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRetry}
            >
              Retry
            </Button>
          </Box>
        ) : (
          <DataGrid
            rows={filteredData}
            columns={columns}
            autoHeight
            rowHeight={40}
            columnHeaderHeight={40}
            paginationModel={paginationModel}
            onPaginationModelChange={(newModel) => {
              console.log('📄 Review page changed to:', newModel.page, 'pageSize:', newModel.pageSize);
              console.log('🔍 Current filters:', { statusFilter, searchQuery });
              console.log('📊 Filtered review items count:', filteredData.length);
              setPaginationModel(newModel);
            }}
            paginationMode="client"
            rowCount={filteredData.length}
            pageSizeOptions={[30, 50, 100]}
            onRowClick={(params) => navigate(`/analysis/${params.row.order_number}/${params.row.test_identifier}`)}
            disableColumnSelector
            disableDensitySelector
            sx={{
              border: "none",
              borderRadius: 0,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-row": {
                cursor: "pointer",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f9f9f9",
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export default Review;
